import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, MessageSquare, CheckCircle2, XCircle, Clock, Eye } from "lucide-react";
import { format } from "date-fns";
import { Separator } from "@/components/ui/separator";

type FeedbackStatus = "pending" | "under_review" | "accepted" | "rejected" | "resolved";
type FeedbackType = "incorrect_answer" | "unclear_question" | "typo" | "outdated_info" | "missing_explanation" | "other";

interface Feedback {
  id: string;
  mcq_id: string;
  feedback_type: FeedbackType;
  feedback_text: string;
  suggested_correction: string | null;
  status: FeedbackStatus;
  admin_response: string | null;
  created_at: string;
  mcqs: {
    stem: string;
  } | null;
}

const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  incorrect_answer: "Incorrect Answer",
  unclear_question: "Unclear Question",
  typo: "Typo",
  outdated_info: "Outdated Info",
  missing_explanation: "Missing Explanation",
  other: "Other",
};

const STATUS_CONFIG = {
  pending: { icon: Clock, color: "text-yellow-600", label: "Pending" },
  under_review: { icon: Eye, color: "text-blue-600", label: "Under Review" },
  accepted: { icon: CheckCircle2, color: "text-green-600", label: "Accepted" },
  rejected: { icon: XCircle, color: "text-red-600", label: "Rejected" },
  resolved: { icon: CheckCircle2, color: "text-primary", label: "Resolved" },
};

export function MyFeedbackHistory() {
  const { user } = useAuth();

  const { data: feedbackList, isLoading } = useQuery({
    queryKey: ["my-feedback", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("mcq_user_feedback")
        .select(`
          *,
          mcqs (stem)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Feedback[];
    },
    enabled: !!user,
  });

  if (!user) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            Please log in to view your feedback history.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          My Feedback History
        </CardTitle>
        <CardDescription>
          View all your MCQ improvement suggestions and their status
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !feedbackList || feedbackList.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>You haven't submitted any feedback yet.</p>
            <p className="text-sm mt-2">
              Use the "Suggest Improvement" button on any MCQ to report issues or suggest changes.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedbackList.map((feedback) => {
              const StatusIcon = STATUS_CONFIG[feedback.status].icon;
              return (
                <div key={feedback.id} className="border rounded-lg p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm line-clamp-2">
                        {feedback.mcqs?.stem || "Question not found"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Submitted {format(new Date(feedback.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusIcon className={`h-4 w-4 ${STATUS_CONFIG[feedback.status].color}`} />
                      <Badge variant="outline">
                        {STATUS_CONFIG[feedback.status].label}
                      </Badge>
                    </div>
                  </div>

                  {/* Feedback Details */}
                  <div>
                    <Badge variant="secondary" className="mb-2">
                      {FEEDBACK_TYPE_LABELS[feedback.feedback_type]}
                    </Badge>
                    <p className="text-sm">{feedback.feedback_text}</p>
                    {feedback.suggested_correction && (
                      <div className="mt-2 p-3 bg-muted rounded-md">
                        <p className="text-xs text-muted-foreground mb-1">Your suggestion:</p>
                        <p className="text-sm">{feedback.suggested_correction}</p>
                      </div>
                    )}
                  </div>

                  {/* Admin Response */}
                  {feedback.admin_response && (
                    <>
                      <Separator />
                      <div className="p-3 bg-primary/5 rounded-md">
                        <p className="text-xs text-muted-foreground mb-1">Team response:</p>
                        <p className="text-sm">{feedback.admin_response}</p>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
